/* eslint-disable react/display-name */
/* eslint-disable no-extra-parens */
import React, { memo, useCallback } from 'react';

import { AirQualityListItem } from './airQualityListItem';
import { TopTriggerIcon } from './topTriggerIcons';
import { IStoreState, Pollutants } from '../../common/interfaces/types';
import { TopTriggersHealthAdviceContainer } from './styles';
import { AqiHealthAdviceAndTopTriggersType, pollutantNameUnits } from './types';
import { AqiHealthAdvicesTopTriggerHeader } from './aqiHealthAdviceTopTriggerHeader';
import { reduxConnect } from '../../../common';
import { getDominantPollutant, getFormattedValue, getGaugeColor, getGaugeFill } from './aqiUtils';
import { numberOfAqiListItemsLoading, pollutantList } from '../constants';
import { HealthAdviceTopTriggerItemLoadingUI } from './loadingUI/healthAdviceTopTriggerItemLoadingUI';
import { NoAqiDataUI } from './noDataUI/noAqiDataUI';
import { aqiNoDataErrorStatusCode } from '../constants';
import { DefaultAqiErrorUI } from './noDataUI/defaultAqiErrorUI';
import type { PollutantType } from './types';
import { useAirQualityProvider } from '../../../hooks/useAirQualityProvider';
interface TopTriggersProps {
    pollutants: Pollutants;
    currentAirQualityIsFetching: boolean;
    isNoDataError: boolean;
    authId: string;
    secret: string;
    aqiBaseUrl: string;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { airQuality, appState } = storeState ?? {};
    const { authId, secret, aqiBaseUrl } = appState?.appState || {};
    const { currentAirQuality, currentAirQualityIsFetching, lastError } = airQuality ?? {};
    const { pollutants } = currentAirQuality ?? {};
    const isNoDataError = lastError?.responseStatus === aqiNoDataErrorStatusCode;

    return {
        pollutants,
        currentAirQualityIsFetching,
        isNoDataError,
        authId,
        secret,
        aqiBaseUrl
    };
};

const mapDispatchToProps = {};

export const TopTriggers = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({ pollutants, currentAirQualityIsFetching, isNoDataError, authId, secret, aqiBaseUrl }: TopTriggersProps) => {

    const { pollutantTypes } = useAirQualityProvider(aqiBaseUrl, authId, secret);

    const renderList = useCallback((airQualityPollutants: Pollutants, isLoading: boolean, noDataError: boolean) => {
        if (isLoading) {
            const loadingList = numberOfAqiListItemsLoading(6);
            return (
                loadingList.map((loadingItemNumber: number, index: number) => {
                    return (
                        <HealthAdviceTopTriggerItemLoadingUI key={loadingItemNumber} showDivider={index !== (loadingList.length - 1)} />
                    );
                })
            );
        } else if (airQualityPollutants) {
            return (
                pollutantList.map((pollutantId: string, index: number) => {
                    const pollutantNameUnitInfo = pollutantNameUnits?.[pollutantId as unknown as keyof typeof PollutantType];
                    const showDivider = index !== (pollutantList.length - 1);
                    const pollutant = airQualityPollutants[pollutantId as unknown as keyof typeof PollutantType];
                    const pollutantTitle = getDominantPollutant(pollutantNameUnitInfo.formula) ?? '';
                    const gaugeFill = getGaugeFill(pollutantTypes, pollutantId as keyof typeof PollutantType, pollutant?.concentration);
                    const gaugeColor = getGaugeColor(gaugeFill);
                    const pollutantConcentrationDisplayText = pollutant?.units && Number.isFinite(pollutant?.concentration)
                        ? `${getFormattedValue(pollutant?.concentration)} ${pollutant?.units}`
                        : '';

                    return (
                        <AirQualityListItem
                            key={pollutantId}
                            pollutantType={pollutantId as keyof typeof PollutantType}
                            itemIcon={<TopTriggerIcon
                                pollutantIconHeight={13}
                                pollutantIconWidth={13} 
                                gaugeColor={gaugeColor}
                            />}
                            itemTitle={pollutantTitle}
                            pollutantConcentration={pollutantConcentrationDisplayText}
                            showDivider={showDivider}
                        />
                    );
                })   
            );
        } else if (noDataError) {
            return <NoAqiDataUI />;
        } else {
            return <DefaultAqiErrorUI />;
        }
    }, []);

    return (
        <TopTriggersHealthAdviceContainer>
            <AqiHealthAdvicesTopTriggerHeader title={AqiHealthAdviceAndTopTriggersType.topTriggers} />
            {renderList(pollutants, currentAirQualityIsFetching, isNoDataError)}
        </TopTriggersHealthAdviceContainer>
    );
}));

TopTriggers.displayName = 'TopTriggers';
